import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../../components';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  RocketOutlined,
  FileTextOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Card, Col, Row, Space, Tag, Typography } from 'antd';
import TemplateCreateEdit from './CreateEdit';
import {
  ICrmTemplate,
  getCrmTemplates,
  deleteCrmTemplate,
} from '../../../http/crm';

const { Text, Paragraph } = Typography;

const Templates: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>('');
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);

  const [templates, setTemplates] = useState<ICrmTemplate[]>([]);
  const [template, setTemplate] = useState<ICrmTemplate | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [categoriesSet, setCategoriesSet] = useState<string[]>([]);
  const [previewTemplate, setPreviewTemplate] = useState<ICrmTemplate | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listTemplates = useCallback(async () => {
    setLoading(true);
    setTemplates([]);
    try {
      const response = await getCrmTemplates(limit, (page - 1) * limit, search, category, isActive);
      setPages(response.count);
      setTemplates(response.data);
      const cats = new Set<string>();
      response.data.forEach((t) => {
        if (t.category) cats.add(t.category);
      });
      setCategoriesSet(Array.from(cats));
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar templates.',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, category, isActive, addToast]);

  const handleDelete = useCallback(async (record: ICrmTemplate) => {
    try {
      await deleteCrmTemplate(record.id);
      addToast({
        type: 'info',
        title: 'Template removido',
        description: 'Template excluído com sucesso.',
      });
      listTemplates();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao excluir template.',
      });
    }
  }, [addToast, listTemplates]);

  const extractPlaceholders = (body: string): string[] => {
    const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const matches: string[] = [];
    let m;
    while ((m = regex.exec(body)) !== null) {
      if (!matches.includes(m[1])) matches.push(m[1]);
    }
    return matches;
  };

  useEffect(() => {
    listTemplates();
  }, [listTemplates]);

  return (
    <PageLayout>
      <PageIntro
        badge="CRM"
        icon={<FileTextOutlined />}
        title="Templates de Mensagem"
        description="Crie modelos reutilizáveis de mensagens de WhatsApp. Use placeholders como \{\{cliente\}\} para personalização."
      />
      <MenuOptions
        showInputSearch
        search={search}
        setSearch={setSearch}
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listTemplates(),
          },
          permissionFind([PermissionActions.crmTemplatesCreate])
            ? {
                label: 'Cadastrar Template',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setTemplate(null);
                  setModalCreate(true);
                },
              }
            : null,
          ...(screenFind([Screens.crmCampaignsView])
            ? [
                {
                  label: 'Campanhas',
                  key: '3',
                  icon: <RocketOutlined />,
                  onClick: () => navigate('/crm/campaigns'),
                },
              ]
            : []),
          ...(screenFind([Screens.crmMessagingView])
            ? [
                {
                  label: 'Inbox / Mensagens',
                  key: '4',
                  icon: <EyeOutlined />,
                  onClick: () => navigate('/crm/messaging'),
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card title="Categoria" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!category}
                  onChange={() => setCategory(undefined)}
                >
                  Todas
                </Tag.CheckableTag>
                {categoriesSet.map((c) => (
                  <Tag.CheckableTag
                    key={c}
                    checked={category === c}
                    onChange={() => setCategory(category === c ? undefined : c)}
                  >
                    {c}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card title="Ativo" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={isActive === undefined}
                  onChange={() => setIsActive(undefined)}
                >
                  Todos
                </Tag.CheckableTag>
                <Tag.CheckableTag
                  checked={isActive === true}
                  onChange={() => setIsActive(isActive === true ? undefined : true)}
                >
                  Sim
                </Tag.CheckableTag>
                <Tag.CheckableTag
                  checked={isActive === false}
                  onChange={() => setIsActive(isActive === false ? undefined : false)}
                >
                  Não
                </Tag.CheckableTag>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card title="Sintaxe" size="small" bordered={false}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Use <Text code>{`{{variavel}}`}</Text> para placeholders.
              </Text>
            </Card>
          </Col>
        </Row>
      </PagePanel>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Nome',
              dataIndex: 'name',
              width: 200,
              render: (value: string, record: ICrmTemplate) => (
                <Space direction="vertical" size={2}>
                  <Text strong>{value}</Text>
                  {record.category && (
                    <Tag style={{ margin: 0 }}>{record.category}</Tag>
                  )}
                </Space>
              ),
            },
            {
              title: 'Assunto',
              dataIndex: 'subject',
              width: 180,
              render: (value: string | null | undefined) =>
                value ? (
                  <Text>{value}</Text>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
            {
              title: 'Conteúdo',
              dataIndex: 'body_text',
              render: (value: string) => {
                const placeholders = extractPlaceholders(value);
                return (
                  <div>
                    <Paragraph
                      ellipsis={{ rows: 2, expandable: false }}
                      style={{ marginBottom: 6 }}
                    >
                      {value}
                    </Paragraph>
                    {placeholders.length > 0 && (
                      <Space wrap size={[4, 4]}>
                        {placeholders.map((p) => (
                          <Tag key={p} color="blue" style={{ fontSize: 11 }}>
                            {'\u007b\u007b'}{p}{'\u007d\u007d'}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Atualizado',
              dataIndex: 'updated_at',
              width: 150,
              render: (value: string) =>
                value ? new Date(value).toLocaleDateString('pt-BR') : '-',
            },
            {
              title: 'Status',
              dataIndex: 'is_active',
              width: 90,
              render: (value: boolean) => <StatusActive status={!!value} />,
            },
            {
              title: 'Opções',
              width: 170,
              render: (_: unknown, record: ICrmTemplate) => (
                <DropdownOptions
                  items={[
                    {
                      label: 'Visualizar',
                      key: '1',
                      icon: <EyeOutlined />,
                      onClick: () => setPreviewTemplate(record),
                    },
                    permissionFind([PermissionActions.crmTemplatesUpdate])
                      ? {
                          label: 'Editar',
                          key: '2',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setTemplate(record);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    permissionFind([PermissionActions.crmTemplatesUpdate])
                      ? {
                          label: 'Excluir',
                          key: '3',
                          icon: <EditOutlined />,
                          danger: true,
                          onClick: () => {
                            if (
                              window.confirm(
                                `Tem certeza que deseja excluir o template "${record.name}"?`,
                              )
                            ) {
                              handleDelete(record);
                            }
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={templates}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {modalCreate && (
        <TemplateCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          template={template}
          setTemplates={setTemplates}
          onSaved={listTemplates}
        />
      )}
      {previewTemplate && (
        <Card
          title={`Visualizar: ${previewTemplate.name}`}
          size="small"
          style={{ display: 'none' }}
        >
          <Paragraph>{previewTemplate.body_text}</Paragraph>
        </Card>
      )}
    </PageLayout>
  );
};

export default Templates;
